import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeaveGuard, useSearchParams } from '@shared/hooks';
import { EDetectionPollingStatus, IBaseDetectionRun, TPollDetectionReturn } from '../types/shared.types';
import { FAILED_DETECTION_STATUS } from '../constants';
import { useCancelInlineBlockDetection } from '../api/inlineBlocks.api';
import { useCancelReferenceDetection, usePollReferenceDetection } from '../api/references.api';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { IReferenceDetectionRun } from '../types/references.types';

// UseQueryResult<TPollDetectionReturn<IReferenceDetectionRun> | undefined, any>
type TPollHook<IDetectionRun extends IBaseDetectionRun> = (
  runId: string | undefined
) => UseQueryResult<TPollDetectionReturn<IDetectionRun> | undefined, any>;

type TCancelHook = () => UseMutationResult<
  void,
  unknown,
  {
    runId: string;
  },
  unknown
>;

type TProps<IDetectionRun extends IBaseDetectionRun> = {
  runId: string;
  usePoll: TPollHook<IDetectionRun>;
  useCancel: TCancelHook;
  onCancelDone: () => void; // what to do when cancelling is done (or analysis already failed)
  leaveGuardMessage: string;
  summaryNavigateTo: (runId: string) => string;
};

const useDetectionPreviewLifecycle = <IDetectionRun extends IBaseDetectionRun>({
  runId,
  usePoll,
  useCancel,
  onCancelDone,
  leaveGuardMessage,
  summaryNavigateTo,
}: TProps<IDetectionRun>) => {
  const navigate = useNavigate();
  const { searchParams, change } = useSearchParams();

  const pollQuery = usePoll(runId);
  const { data: detectionData } = pollQuery;
  const [run, resolvedItems] =
    detectionData?.status === EDetectionPollingStatus.READY ? [detectionData.run, detectionData.resolvedItems] : [];

  // compute once per change
  const isAnalysisFailed = !!run && FAILED_DETECTION_STATUS.includes(run?.status);

  // summary dialog state + helper
  const [isPollingDialogOpen, setIsPollingDialogOpen] = useState(false);
  const openPollingDialog = useCallback(async () => {
    await pollQuery.refetch();
    setIsPollingDialogOpen(true);
  }, [pollQuery]);

  // leave-guard + "?wide" dance
  const [skipGuard, setSkipGuard] = useState(false);
  const isNavOpen = searchParams.get('wide') === null;

  useEffect(() => {
    if (detectionData && isNavOpen) setSkipGuard(true);
  }, [detectionData, isNavOpen]);

  useEffect(() => {
    if (isPollingDialogOpen && detectionData?.status === EDetectionPollingStatus.READY) setSkipGuard(true);
  }, [isPollingDialogOpen, detectionData?.status]);

  const cancelMutation = useCancel();
  const handleCancelDetection = useCallback(() => {
    console.log('CANCELLING DETECTION', runId);
    const onSuccess = onCancelDone;
    if (isAnalysisFailed) onSuccess();
    else
      cancelMutation.mutate(
        { runId },
        {
          onSuccess: () => {
            console.log('cancelled');
            onSuccess();
          },
        }
      );
  }, [cancelMutation, runId, onCancelDone, isAnalysisFailed]);

  useLeaveGuard(!skipGuard, handleCancelDetection, leaveGuardMessage);

  /**
   * keep the following `useEffect` after the leave-guard because they depend on it's `enabled` state!
   */
  useEffect(() => {
    if (skipGuard) {
      if (isPollingDialogOpen) {
        // if summary is open, navigate to the summary page
        navigate(summaryNavigateTo(runId));
      } else {
        // set ?wide=true and re-enable the guard
        change('wide', 'true');
        setSkipGuard(false);
      }
    }
  }, [skipGuard, isPollingDialogOpen, runId, change, navigate, summaryNavigateTo]);

  return {
    // shared state/ops for the page
    pollQuery,
    run,
    resolvedItems,
    isAnalysisFailed,
    isPollingDialogOpen,
    setIsPollingDialogOpen,
    openPollingDialog,
    cancelMutation,
    handleCancelDetection,
  } as const;
};

export default useDetectionPreviewLifecycle;
